import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { useScrollAnimation, useStaggerAnimation } from '../../hooks/useScrollAnimation';

const offices = [
  {
    name: 'Head Office',
    city: 'Mississauga',
    address: '2355 Derry Road East, Unit 1',
    cityProvince: 'Mississauga, ON L5S 1V6',
    phone: '(905) 696-0613',
    phoneRaw: '9056960613',
    hours: 'Mon–Fri: 8:00 AM – 6:00 PM',
    mapQuery: '2355+Derry+Road+East+Mississauga+ON+L5S+1V6',
    icon: 'ri-building-2-line',
    badge: 'HEAD OFFICE',
  },
  {
    name: 'Hamilton Office',
    city: 'Hamilton',
    address: '1 Hunter Street East, Ground Floor',
    cityProvince: 'Hamilton, ON L8N 3W1',
    phone: '1-888-458-9181',
    phoneRaw: '18884589181',
    hours: 'Mon–Fri: 9:00 AM – 5:00 PM',
    mapQuery: '1+Hunter+Street+East+Hamilton+ON+L8N+3W1',
    icon: 'ri-store-2-line',
    badge: 'SALES & SERVICE',
  },
  {
    name: 'Whitby Office',
    city: 'Whitby',
    address: 'Durham Corporate Centre',
    cityProvince: 'Whitby, ON L1N 1C4',
    phone: '1-888-458-9181',
    phoneRaw: '18884589181',
    hours: 'Mon–Fri: 9:00 AM – 5:00 PM',
    mapQuery: 'Durham+Corporate+Centre+Whitby+ON+L1N+1C4',
    icon: 'ri-store-2-line',
    badge: 'SALES & SERVICE',
  },
];

function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);

  const serviceAreas = [
    { name: 'Toronto', path: '/service-areas/toronto' },
    { name: 'Mississauga', path: '/service-areas/mississauga' },
    { name: 'Brampton', path: '/service-areas/brampton' },
    { name: 'Oakville', path: '/service-areas/oakville' },
    { name: 'Hamilton', path: '/service-areas/hamilton' },
    { name: 'Richmond Hill', path: '/service-areas/richmond-hill' },
    { name: 'Vaughan', path: '/service-areas/vaughan' },
  ];

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628]">
            <span
              className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              ALLIANCE
            </span>
            <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">
              SECURITY SYSTEMS
            </span>
          </div>
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
        >
          <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
        </button>
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/camera-packages"
            className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
          >
            Cameras
          </Link>
          <Link
            to="/access-control"
            className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
          >
            Access Control
          </Link>
          <Link
            to="/alarm-packages"
            className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
          >
            Alarm Packages
          </Link>
          <Link
            to="/live-monitoring"
            className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
          >
            Live Monitoring
          </Link>
          <Link
            to="/clients"
            className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
          >
            Clients
          </Link>
          <Link
            to="/faq"
            className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
          >
            FAQ
          </Link>
          <div
            className="relative"
            onMouseEnter={() => setServiceAreasOpen(true)}
            onMouseLeave={() => setServiceAreasOpen(false)}
          >
            <button className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
              Service Areas
              <i
                className={`ri-arrow-down-s-line text-base transition-transform ${
                  serviceAreasOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            {serviceAreasOpen && (
              <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                {serviceAreas.map((area) => (
                  <Link
                    key={area.path}
                    to={area.path}
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#D4A853]/10 hover:text-[#D4A853] transition-colors cursor-pointer"
                  >
                    {area.name}
                  </Link>
                ))}
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <Link
                    to="/service-areas"
                    className="block px-4 py-2.5 text-sm font-semibold text-[#D4A853] hover:bg-[#D4A853]/10 transition-colors cursor-pointer"
                  >
                    View All Areas →
                  </Link>
                </div>
              </div>
            )}
          </div>
          <Link
            to="/contact"
            className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
          >
            Contact Us
          </Link>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
          <Link
            to="/camera-packages"
            className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
          >
            Camera Packages
          </Link>
          <Link
            to="/access-control"
            className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
          >
            Access Control
          </Link>
          <Link
            to="/alarm-packages"
            className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
          >
            Alarm Packages
          </Link>
          <Link
            to="/live-monitoring"
            className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
          >
            Live Monitoring
          </Link>
          <Link
            to="/clients"
            className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
          >
            Clients
          </Link>
          <Link
            to="/faq"
            className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
          >
            FAQ
          </Link>
          <div className="border-b border-gray-50">
            <button
              onClick={() => setMobileServiceAreasOpen(!mobileServiceAreasOpen)}
              className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] cursor-pointer"
            >
              Service Areas
              <i
                className={`ri-arrow-down-s-line text-base transition-transform ${
                  mobileServiceAreasOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            {mobileServiceAreasOpen && (
              <div className="pl-4 pb-3 space-y-1">
                {serviceAreas.map((area) => (
                  <Link
                    key={area.path}
                    to={area.path}
                    className="block py-2 text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer"
                  >
                    {area.name}
                  </Link>
                ))}
                <Link
                  to="/service-areas"
                  className="block py-2 text-sm font-semibold text-[#D4A853] hover:text-[#B8923D] transition-colors cursor-pointer"
                >
                  View All Areas →
                </Link>
              </div>
            )}
          </div>
          <Link
            to="/contact"
            className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2"
          >
            Contact Us
          </Link>
        </div>
      )}
    </nav>
  );
}

function OfficeCard({ office, index }: { office: typeof offices[0]; index: number }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center">
          <i className={`${office.icon} text-2xl text-[#D4A853]`}></i>
        </div>
        <span className="bg-[#0A1628] text-white px-3 py-1 rounded-full text-[10px] font-bold tracking-wider whitespace-nowrap">
          {office.badge}
        </span>
      </div>
      <h3 className="text-xl font-bold text-[#0A1628] mb-1">{office.name}</h3>
      <p className="text-sm text-[#D4A853] font-semibold mb-4">{office.city}</p>
      <div className="space-y-3 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-map-pin-line text-gray-400"></i>
          </div>
          <div>
            <p className="text-sm text-gray-700">{office.address}</p>
            <p className="text-sm text-gray-700">{office.cityProvince}</p>
          </div>
        </div>
        <a href={`tel:${office.phoneRaw}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-phone-line text-gray-400"></i>
          </div>
          {office.phone}
        </a>
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-700">{office.hours}</p>
        </div>
      </div>
      <a
        href={`https://www.google.com/maps/search/?api=1&query=${office.mapQuery}`}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="flex items-center justify-center gap-2 w-full bg-[#0A1628] text-white py-3 rounded-xl text-sm font-semibold hover:bg-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
      >
        <i className="ri-direction-line text-base"></i>
        Get Directions
      </a>
    </div>
  );
}

// ============================================================
// 🔗 CRM WEBHOOK INTEGRATION
// Paste your Zapier / Make webhook URL below.
// Leave empty ("") to disable.
// ============================================================
const ZAPIER_WEBHOOK_URL = '';

export default function ContactPage() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeMap, setActiveMap] = useState(0);
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [callbackSuccess, setCallbackSuccess] = useState(false);
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);

  const heroAnim = useScrollAnimation({ threshold: 0.05 });
  const mapAnim = useScrollAnimation({ threshold: 0.1 });
  const officeCards = useStaggerAnimation(3);
  const formAnim = useScrollAnimation({ threshold: 0.1 });
  const trustAnim = useStaggerAnimation(3);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      return;
    }

    const payload = {
      name: (formData.get('name') as string) ?? '',
      email: (formData.get('email') as string) ?? '',
      phone: (formData.get('phone') as string) ?? '',
      preferred_office: (formData.get('preferred_office') as string) ?? '',
      subject: (formData.get('subject') as string) ?? '',
      message: message || '',
      source: 'Contact Page',
      submitted_at: new Date().toISOString(),
    };

    const data = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => data.append(key, value));

    try {
      const response = await fetch('https://readdy.ai/api/form/d6hp5p857726vudt81o0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (response.ok) {
        // Fire webhook to CRM (Zapier / Make / HubSpot etc.)
        if (ZAPIER_WEBHOOK_URL) {
          try {
            await fetch(ZAPIER_WEBHOOK_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
          } catch (webhookErr) {
            console.warn('Webhook delivery failed (non-blocking):', webhookErr);
          }
        }
        setShowSuccess(true);
        form.reset();
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        console.error('Form submission failed with status:', response.status);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleCallbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCallbackSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    data.append('name', (formData.get('cb_name') as string) ?? '');
    data.append('email', (formData.get('cb_email') as string) ?? '');
    data.append('phone', (formData.get('cb_phone') as string) ?? '');
    data.append('preferred_time', (formData.get('cb_time') as string) ?? '');
    data.append('notes', (formData.get('cb_notes') as string) ?? '');
    data.append('source', 'Callback Request');
    data.append('submitted_at', new Date().toISOString());
    try {
      const res = await fetch('https://readdy.ai/api/form/d6m87n2qoe30lj0v88v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setCallbackSuccess(true);
        form.reset();
        setTimeout(() => {
          setCallbackSuccess(false);
          setShowCallbackModal(false);
        }, 4000);
      }
    } catch (err) {
      console.error('Callback form error:', err);
    } finally {
      setCallbackSubmitting(false);
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Alliance Security Systems',
    telephone: '1-888-458-9181',
    url: 'https://alliancesecuritysystems.com',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+1-905-696-0613',
        contactType: 'customer service',
        areaServed: 'CA',
        availableLanguage: 'English',
      },
      {
        '@type': 'ContactPoint',
        telephone: '+1-888-458-9181',
        contactType: 'sales',
        areaServed: 'CA',
        availableLanguage: 'English',
      },
    ],
    location: [
      {
        '@type': 'Place',
        name: 'Alliance Security Systems - Head Office',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2355 Derry Road East, Unit 1',
          addressLocality: 'Mississauga',
          addressRegion: 'ON',
          postalCode: 'L5S 1V6',
          addressCountry: 'CA',
        },
      },
      {
        '@type': 'Place',
        name: 'Alliance Security Systems - Hamilton Office',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1 Hunter Street East, Ground Floor',
          addressLocality: 'Hamilton',
          addressRegion: 'ON',
          postalCode: 'L8N 3W1',
          addressCountry: 'CA',
        },
      },
      {
        '@type': 'Place',
        name: 'Alliance Security Systems - Whitby Office',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Durham Corporate Centre',
          addressLocality: 'Whitby',
          addressRegion: 'ON',
          postalCode: 'L1N 1C4',
          addressCountry: 'CA',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Us - Office Locations Mississauga Hamilton Whitby | Alliance Security Systems"
        description="Contact Alliance Security Systems for alarm systems, security cameras, and access control. Visit our offices in Mississauga, Hamilton, or Whitby. Call 1-888-458-9181 for a free security consultation."
        keywords="contact Alliance Security, security company Mississauga, alarm systems Hamilton, security cameras Whitby, GTA security office"
        canonical="https://alliancesecuritysystems.com/contact"
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/22819622092d4fb7f7e9973cc69cdae7.jpg"
            alt="Alliance Security Office"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/75 to-[#0A1628]/50"></div>
        </div>
        <div
          ref={heroAnim.ref}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6"
          style={{
            opacity: heroAnim.isVisible ? 1 : 0,
            transform: heroAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <div className="max-w-3xl">
            <span className="inline-block bg-[#D4A853] text-white px-5 py-2 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
              GET IN TOUCH
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-5 leading-tight">
              Contact Our <strong>Security Experts</strong>
            </h1>
            <p className="text-lg sm:text-xl text-white/85 mb-8 leading-relaxed max-w-2xl">
              Visit any of our three offices across the Greater Toronto Area or reach out online. We respond within 2 hours during business hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="tel:18884589181"
                className="inline-flex items-center justify-center gap-2 bg-[#D4A853] text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg"
              >
                <i className="ri-phone-line text-lg"></i>
                Call 1-888-458-9181
              </a>
              <a
                href="#contact-form-section"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('contact-form-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-white hover:text-[#0A1628] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line text-lg"></i>
                Send a Message
              </a>
            </div>
          </div>
        </div>
      </section>

      <main>
        {/* Map Section */}
        <section className="py-14 sm:py-20 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div
              ref={mapAnim.ref}
              className="text-center mb-10"
              style={{
                opacity: mapAnim.isVisible ? 1 : 0,
                transform: mapAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease, transform 0.7s ease',
              }}
            >
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                OUR LOCATIONS
              </span>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Three convenient locations serving the Greater Toronto Area, Hamilton, and Durham Region
              </p>
            </div>

            {/* Map Tabs */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-6 flex-wrap">
              {offices.map((office, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveMap(idx)}
                  className={`px-4 sm:px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    activeMap === idx
                      ? 'bg-[#0A1628] text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-[#D4A853]/10 hover:text-[#D4A853] border border-gray-200'
                  }`}
                >
                  <i className="ri-map-pin-2-fill mr-1.5 text-[#D4A853]"></i>
                  {office.city}
                </button>
              ))}
            </div>

            {/* Map */}
            <div className="w-full h-[350px] sm:h-[450px] rounded-2xl overflow-hidden shadow-xl border border-gray-200">
              <iframe
                title={`${offices[activeMap].name} Location`}
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${offices[activeMap].mapQuery}&zoom=14`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>

            {/* Office Cards */}
            <div ref={officeCards.ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              {offices.map((office, index) => (
                <div key={index} style={officeCards.getItemStyle(index)}>
                  <OfficeCard office={office} index={index} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-form-section" className="py-14 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              {/* Left - Info */}
              <div
                ref={formAnim.ref}
                style={{
                  opacity: formAnim.isVisible ? 1 : 0,
                  transform: formAnim.isVisible ? 'translateX(0)' : 'translateX(-40px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                }}
              >
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                  SEND A MESSAGE
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4">
                  Let Us Help Secure Your Property
                </h2>
                <p className="text-base text-gray-600 mb-8 leading-relaxed">
                  Fill out the form and one of our security experts will get back to you within 2 hours during business hours. We offer free consultations and security assessments.
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-xl text-[#D4A853]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1628] mb-1">
                        <a href="tel:18884589181" className="hover:text-[#D4A853] transition-colors cursor-pointer">
                          Call Us Anytime
                        </a>
                      </h4>
                      <a href="tel:18884589181" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">
                        1-888-458-9181 (Toll Free)
                      </a>
                      <br />
                      <a href="tel:9056960613" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">
                        (905) 696-0613 (Head Office)
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-xl text-[#D4A853]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1628] mb-1">
                        <a href="mailto:info@alliance-security.com" className="hover:text-[#D4A853] transition-colors cursor-pointer">
                          Email Us
                        </a>
                      </h4>
                      <a href="mailto:info@alliance-security.com" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">
                        info@alliance-security.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-xl text-[#D4A853]"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0A1628] mb-1">Business Hours</h4>
                      <p className="text-sm text-gray-600">Monday – Friday: 8:00 AM – 6:00 PM</p>
                      <p className="text-sm text-gray-600">Saturday: By Appointment</p>
                      <p className="text-sm text-gray-600">24/7 Emergency Monitoring</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0A1628] rounded-2xl p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#D4A853] rounded-full flex items-center justify-center">
                      <i className="ri-shield-check-line text-xl text-white"></i>
                    </div>
                    <h4 className="text-white font-bold text-lg">Free Security Assessment</h4>
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mb-4">
                    Not sure what you need? Our experts will visit your property, assess vulnerabilities, and recommend the best security solution — completely free.
                  </p>
                  <a
                    href="tel:18884589181"
                    className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-check-line"></i>
                    Book Your Free Assessment
                  </a>
                </div>
              </div>

              {/* Right - Form */}
              <div>
                <div className="bg-[#F8F9FA] rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 border border-gray-100">
                  {showSuccess && (
                    <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-5 py-4 rounded-xl flex items-start gap-3">
                      <i className="ri-check-double-line text-green-600 text-xl mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-sm">Message sent successfully!</p>
                        <p className="text-xs mt-1">We'll get back to you within 2 hours during business hours.</p>
                      </div>
                    </div>
                  )}

                  <form className="space-y-5" data-readdy-form id="contact-page-form" onSubmit={handleSubmit}>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                          placeholder="john@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                          placeholder="(905) 123-4567"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Office</label>
                      <select
                        name="preferred_office"
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm cursor-pointer"
                      >
                        <option value="">Select an office (optional)</option>
                        <option value="Mississauga - Head Office">Mississauga – Head Office</option>
                        <option value="Hamilton Office">Hamilton Office</option>
                        <option value="Whitby Office">Whitby Office</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Subject *</label>
                      <select
                        name="subject"
                        required
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm cursor-pointer"
                      >
                        <option value="">Select a subject</option>
                        <option value="Free Security Assessment">Free Security Assessment</option>
                        <option value="Alarm Systems">Alarm Systems</option>
                        <option value="Security Cameras">Security Cameras</option>
                        <option value="Access Control">Access Control</option>
                        <option value="Live Monitoring">Live Monitoring</option>
                        <option value="Service & Support">Service & Support</option>
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm resize-none"
                        placeholder="Tell us about your security needs..."
                      ></textarea>
                      <p className="text-xs text-gray-400 mt-1.5">Maximum 500 characters</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-[#D4A853] text-white py-3.5 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg flex items-center justify-center gap-2"
                    >
                      <i className="ri-send-plane-line text-lg"></i>
                      Send Message
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-14 sm:py-20 bg-[#F8F9FA]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div ref={trustAnim.ref} className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-shield-check-line',
                  title: 'Licensed & Insured',
                  description: '100% licensed and fully insured for your complete protection and peace of mind.',
                },
                {
                  icon: 'ri-time-line',
                  title: '2-Hour Response',
                  description: 'We respond to all inquiries within 2 hours during business hours. Emergency support 24/7.',
                },
                {
                  icon: 'ri-award-line',
                  title: '25 Years of Trust',
                  description: 'Over 30,000 installations since 1999. BBB A+ rated security provider.',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 sm:p-8 text-center shadow-md border border-gray-100 hover:shadow-lg transition-all"
                  style={trustAnim.getItemStyle(index)}
                >
                  <div className="w-14 h-14 bg-[#D4A853]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${item.icon} text-2xl text-[#D4A853]`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white pt-14 sm:pt-20 pb-8 sm:pb-12 mx-3 sm:mx-6 rounded-t-[24px] sm:rounded-t-[32px] relative overflow-hidden">
        <div className="absolute inset-0 text-[60px] sm:text-[80px] md:text-[120px] font-extrabold text-white/5 text-center leading-none pb-4">
          SECURITY
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12">
            <div>
              <div className="inline-block border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628] mb-4">
                <div className="flex flex-col leading-none">
                  <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    ALLIANCE
                  </span>
                  <span className="text-[8px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">
                    SECURITY SYSTEMS
                  </span>
                </div>
              </div>
              <p className="text-white/60 mb-4 sm:mb-6 text-sm">Trusted Security Since 1999</p>
              <a
                href="tel:18884589181"
                className="inline-block bg-[#D4A853] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>Emergency: 1-888-458-9181
              </a>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Head Office</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-[#D4A853] text-lg flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-white/80 leading-relaxed">2355 Derry Road East, Unit 1</p>
                    <p className="text-white/80">Mississauga, ON L5S 1V6</p>
                  </div>
                </div>
                <a href="tel:9056960613" className="flex items-center gap-3 text-white/80 hover:text-[#D4A853] transition-colors cursor-pointer">
                  <i className="ri-phone-line text-[#D4A853] text-lg"></i>(905) 696-0613
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Sales & Service Offices</h3>
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-[#D4A853] font-semibold mb-2">Hamilton Office</p>
                  <div className="flex items-start gap-3 mb-2">
                    <i className="ri-map-pin-line text-white/60 text-base flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-white/80 leading-relaxed">1 Hunter Street East, Ground Floor</p>
                      <p className="text-white/80">Hamilton, ON L8N 3W1</p>
                    </div>
                  </div>
                  <a href="tel:18884589181" className="flex items-center gap-2 text-white/80 hover:text-[#D4A853] transition-colors cursor-pointer">
                    <i className="ri-phone-line text-white/60 text-base"></i>1-888-458-9181
                  </a>
                </div>
                <div>
                  <p className="text-[#D4A853] font-semibold mb-2">Whitby Office</p>
                  <div className="flex items-start gap-3 mb-2">
                    <i className="ri-map-pin-line text-white/60 text-base flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-white/80 leading-relaxed">Durham Corporate Centre</p>
                      <p className="text-white/80">Whitby, ON L1N 1C4</p>
                    </div>
                  </div>
                  <a href="tel:18884589181" className="flex items-center gap-2 text-white/80 hover:text-[#D4A853] transition-colors cursor-pointer">
                    <i className="ri-phone-line text-white/60 text-base"></i>1-888-458-9181
                  </a>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold mb-4 sm:mb-6 text-white">Quick Links</h3>
              <div className="space-y-2 text-sm">
                <Link to="/" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  Home
                </Link>
                <Link to="/alarm-packages" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  Alarm Packages
                </Link>
                <Link to="/camera-packages" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  Camera Packages
                </Link>
                <Link to="/access-control" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  Access Control
                </Link>
                <Link to="/live-monitoring" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  Live Monitoring
                </Link>
                <Link to="/service-areas" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  Service Areas
                </Link>
                <Link to="/blog" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  Blog
                </Link>
                <Link to="/faq" className="block text-white/70 hover:text-[#D4A853] transition-colors cursor-pointer">
                  FAQ
                </Link>
              </div>
            </div>
          </div>
          <div className="pt-6 sm:pt-8 border-t border-white/10 flex flex-col items-center gap-4">
            <p className="text-[#9CA3AF] text-xs sm:text-sm">&copy; 2024 Alliance Security Systems. All rights reserved.</p>
            <div className="flex items-center gap-4 sm:gap-8">
              <Link to="/privacy-policy" className="text-[#9CA3AF] hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">
                Privacy Policy
              </Link>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[#9CA3AF] hover:text-white text-xs sm:text-sm transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Engagement Bar */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-[#0A1628] rounded-full px-5 py-3 shadow-2xl border border-white/10">
        <button
          onClick={() => {
            const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
            if (btn) btn.click();
          }}
          className="flex items-center gap-2 bg-[#D4A853] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-md"
        >
          <i className="ri-chat-3-line text-base"></i>
          Live Chat
        </button>
        <div className="w-px h-6 bg-white/20"></div>
        <button
          onClick={() => setShowCallbackModal(true)}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-phone-incoming-line text-base"></i>
          Request a Callback
        </button>
      </div>

      {/* Callback Request Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCallbackModal(false)}
          ></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-7 z-10 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#D4A853]/10 rounded-xl flex items-center justify-center">
                  <i className="ri-phone-incoming-line text-xl text-[#D4A853]"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#0A1628]">Request a Callback</h3>
                  <p className="text-xs text-gray-500">We'll call you back within 2 hours</p>
                </div>
              </div>
              <button
                onClick={() => setShowCallbackModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {callbackSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-double-line text-3xl text-green-600"></i>
                </div>
                <h4 className="text-lg font-bold text-[#0A1628] mb-2">Callback Requested!</h4>
                <p className="text-sm text-gray-600">One of our security experts will call you back shortly.</p>
              </div>
            ) : (
              <form data-readdy-form id="callback-request-form" onSubmit={handleCallbackSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    name="cb_name"
                    required
                    placeholder="John Doe"
                    className="w-full px-4 py-2.5 bg-[#F8F9FA] border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    name="cb_phone"
                    required
                    placeholder="(905) 123-4567"
                    className="w-full px-4 py-2.5 bg-[#F8F9FA] border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    name="cb_email"
                    placeholder="john@example.com"
                    className="w-full px-4 py-2.5 bg-[#F8F9FA] border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Preferred Callback Time</label>
                  <select
                    name="cb_time"
                    className="w-full px-4 py-2.5 bg-[#F8F9FA] border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm cursor-pointer"
                  >
                    <option value="">Any time during business hours</option>
                    <option value="Morning (8AM–12PM)">Morning (8AM – 12PM)</option>
                    <option value="Afternoon (12PM–3PM)">Afternoon (12PM – 3PM)</option>
                    <option value="Late Afternoon (3PM–6PM)">Late Afternoon (3PM – 6PM)</option>
                    <option value="ASAP">As soon as possible</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Brief Note (optional)</label>
                  <textarea
                    name="cb_notes"
                    rows={2}
                    maxLength={500}
                    placeholder="e.g. Interested in alarm systems for my home..."
                    className="w-full px-4 py-2.5 bg-[#F8F9FA] border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4A853] text-sm resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={callbackSubmitting}
                  className="w-full bg-[#D4A853] text-white py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-md flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {callbackSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-phone-incoming-line text-base"></i>
                      Request Callback
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-gray-400">
                  Mon–Fri 8AM–6PM · Emergency line available 24/7
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
